
import React, { useState, useEffect, useCallback } from 'react';
import Footer from '../Component/NewFooter';
import SplashScreen from '../Component/SplashScreen';
import axios from 'axios';

const BASE_URL      = "https://api.jeyshawnng.com";
const FETCH_TIMEOUT = 8000;
const CACHE_KEY      = "jeyshawn_project_table_data";

/* ── Helpers ───────────────────────────────────────────────────── */
function parseProjectTableData(db) {
  return {
    heroImage:   db?.heroImage   || "",
    closingText: db?.closingText || "",
    projects: Array.isArray(db?.projects)
      ? db.projects
          .slice()
          .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
          .map((p, i) => ({
            id:            p?._id || i,
            project:       p?.project       || "",
            yearCompleted: p?.yearCompleted || "",
            client:        p?.client        || "",
          }))
      : [],
  };
}

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Sub-components ────────────────────────────────────────────── */
function ErrorScreen({ onRetry }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#09090b", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
    }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.1rem", fontFamily: "Georgia, serif", letterSpacing: "0.04em", marginBottom: "8px" }}>
          Poor internet connection.
        </p>
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Please check your connection and try again.
        </p>
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: "8px", padding: "10px 32px", background: "transparent",
          border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "Georgia, serif", transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = "#d4af72"; e.target.style.color = "#d4af72"; }}
        onMouseLeave={e => { e.target.style.borderColor = "rgba(255,255,255,0.25)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}
      >
        Try Again
      </button>
    </div>
  );
}

const isGrayRow = (rowIndex) => rowIndex % 2 === 0;

/* ── Page ──────────────────────────────────────────────────────── */
const ProjectTable = () => {
  const cached = readCache();

  const [status, setStatus]     = useState(cached ? "ready" : "splash-loading");
  const [pageData, setPageData] = useState(cached || null);

  const doFetch = useCallback(async (background = false) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/project-table`, { signal: controller.signal });
      clearTimeout(timeoutId);

      const parsed = parseProjectTableData(data?.data);
      writeCache(parsed);
      setPageData(parsed);

      if (!background) setStatus("splash-done");
    } catch (err) {
      clearTimeout(timeoutId);
      if (background) {
        console.warn("Background refresh failed for /api/project-table:", err.message);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    doFetch(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "error") {
    return <ErrorScreen onRetry={() => { setStatus("splash-loading"); doFetch(false); }} />;
  }
  if (status === "splash-loading") return <SplashScreen blocking />;

  const data = pageData;

  return (
    <div className="color_project">
      {status === "splash-done" && (
        <SplashScreen autoExit onDone={() => setStatus("ready")} />
      )}

      <div className="img_carrierpj">
        {data.heroImage && <img src={data.heroImage} alt="img" />}
      </div>

      <div className="container">
        <div className="table-container">
          <table className="data-table">
            <tbody>
              {data.projects.map((project, projectIndex) => (
                <React.Fragment key={project.id}>
                  <tr className={isGrayRow(0) ? 'row-gray' : 'row-white'}>
                    <td className="col-label">Project:</td>
                    <td className="col-value">{project.project}</td>
                  </tr>

                  <tr className={isGrayRow(2) ? 'row-gray' : 'row-white'}>
                  </tr>

                  <tr className={isGrayRow(3) ? 'row-gray' : 'row-white'}>
                    <td className="col-label">Client:</td>
                    <td className="col-value">
                      {project.client && (
                        <img
                          src={project.client}
                          alt="Client"
                          style={{ width: "200px", height: "80px", objectFit: "contain" }}
                        />
                      )}
                    </td>
                  </tr>

                  <tr className={isGrayRow(1) ? 'row-gray' : 'row-white'}>
                    <td className="col-label">Status:</td>
                    <td className="col-value">{project.yearCompleted}</td>
                  </tr>

                  {projectIndex < data.projects.length - 1 && (
                    <>
                      <tr className="empty-row row-gray">
                        <td className="col-label"></td>
                        <td className="col-value"></td>
                      </tr>
                      <tr className="empty-row row-white">
                        <td className="col-label"></td>
                        <td className="col-value"></td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <p style={{ fontWeight: 'bolder', marginTop: '2rem', fontSize: '20px' }}>
            {data.closingText}
          </p>
        </div>

        <style jsx>{`
          .container {
            max-width: 1000px;
            margin: 0 auto;
            padding: 40px;
            font-family: Arial, Helvetica, sans-serif;
          }
          .color_project {
            background: #e5fce5;
          }
          .table-container {
            width: 100%;
            background: #e5fce5 !important;
          }
          .data-table {
            width: 100%;
            border-collapse: collapse;
            border-spacing: 0;
          }
          .data-table td {
            padding: 14px 16px;
            font-size: 15px;
            font-weight: bold;
            line-height: 1.5;
            border-bottom: 1px solid #000;
            vertical-align: top;
          }
          .row-gray td  { background-color: #f7bb2f; }
          .row-white td { background-color: #e5fce5; }
          .col-label {
            width: 22%;
            color: #444;
            font-weight: normal;
            border-right: 1px solid #000;
          }
          .col-value { width: 78%; color: #333; }
          .empty-row td { height: 32px; padding: 0 16px; }

          @media (max-width: 768px) {
            .container { padding: 20px; }
            .data-table td { padding: 12px 14px; font-size: 12px; }
            .col-label { width: 30%; }
            .col-value { width: 70%; }
          }

          @media (max-width: 480px) {
            .container { padding: 15px; }
            .data-table { display: block; }
            .data-table tbody { display: block; }
            .data-table tr { display: flex; flex-direction: column; margin-bottom: 8px; }
            .col-label { width: 100%; border-right: none; font-weight: 600; padding: 10px 14px; }
            .col-value { width: 100%; padding: 12px 14px; }
            .empty-row { display: none; }
          }
        `}</style>
      </div>
      <Footer />
    </div>
  );
};

export default ProjectTable;